import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AndroidRelease } from '@comms/shared';
import { api } from '../lib/api';
import { APK_URL } from '../lib/appUpdate';
import { androidAppVersion, isAndroidApp } from '../lib/native';
import { useSession } from '../lib/session';
import { Spinner } from '../components/ui';

const mb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** /android: get the Android app from this server (no sign-in needed). */
export function AndroidDownload() {
  const [release, setRelease] = useState<AndroidRelease | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const signedIn = useSession((s) => s.status === 'authenticated');
  const server = window.location.host;

  useEffect(() => {
    api<AndroidRelease>('GET', '/android/latest')
      .then(setRelease)
      .catch(() => setFailed(true));
  }, []);

  const copy = () =>
    void navigator.clipboard?.writeText(server).then(
      () => setCopied(true),
      () => {},
    );

  let body;
  if (failed)
    body = <p className="text-text-2">Couldn&apos;t reach the server. Try again in a moment.</p>;
  else if (!release) body = <Spinner className="mx-auto h-8 w-8" />;
  else if (!release.available)
    body = (
      <p className="text-text-2">
        The Android app isn&apos;t available on this server yet. Ask whoever runs it to set up app
        releases.
      </p>
    );
  else {
    const inAppUpToDate = isAndroidApp() && androidAppVersion() >= release.versionCode;
    body = (
      <>
        {inAppUpToDate ? (
          <p className="rounded bg-bg-1 p-3 text-sm text-text-1">
            You have the latest version ({release.versionName}).
          </p>
        ) : (
          <a
            href={APK_URL}
            download="Comms.apk"
            className="block rounded bg-accent px-4 py-3 text-center text-lg font-semibold text-white hover:bg-accent-hover"
          >
            Download Comms {release.versionName}
          </a>
        )}
        <p className="text-center text-xs text-text-3">
          Android 6 or newer · {mb(release.size)} · released{' '}
          {new Date(release.publishedAt).toLocaleDateString()}
        </p>
        {release.notes && (
          <section>
            <h2 className="mb-1 font-semibold text-text-0">What&apos;s new</h2>
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-text-2">
              {release.notes
                .split('\n')
                .map((l) => l.replace(/^\s*[-*]\s*/, '').trim())
                .filter(Boolean)
                .map((l) => (
                  <li key={l}>{l}</li>
                ))}
            </ul>
          </section>
        )}
        {!isAndroidApp() && (
          <section>
            <h2 className="mb-1 font-semibold text-text-0">Installing</h2>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-text-2">
              <li>
                Open the downloaded <b>Comms.apk</b>. Android asks once to allow installing apps
                from your browser: allow it.
              </li>
              <li>Open Comms and enter this server&apos;s address:</li>
            </ol>
            <div className="mt-2 flex items-center gap-2 pl-5">
              <code className="rounded bg-bg-1 px-2 py-1 text-text-0">{server}</code>
              <button type="button" onClick={copy} className="text-sm text-accent hover:underline">
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-2 pl-5 text-sm text-text-2">
              Updates are offered in the app from then on.
            </p>
          </section>
        )}
        <details className="text-xs text-text-3">
          <summary className="cursor-pointer">File checksum (SHA-256)</summary>
          <code className="mt-1 block break-all">{release.sha256}</code>
        </details>
      </>
    );
  }

  return (
    <div className="safe-y flex min-h-full items-center justify-center overflow-y-auto bg-gradient-to-br from-[#404eed] to-[#23272a] p-4">
      <main className="w-full max-w-md space-y-4 rounded-lg bg-bg-2 p-8 shadow-2xl">
        <div className="text-center">
          <img src="/icons/icon-192.png" alt="" className="mx-auto mb-2 h-16 w-16 rounded-2xl" />
          <h1 className="text-2xl font-bold text-text-0">Comms for Android</h1>
          <p className="mt-1 text-sm text-text-2">
            Calls that ring like phone calls, and notifications even when the app is closed.
          </p>
        </div>
        {body}
        <p className="text-center text-sm">
          <Link to={signedIn ? '/channels/@me' : '/login'} className="text-accent hover:underline">
            {signedIn ? 'Back to Comms' : 'Log in on the web instead'}
          </Link>
        </p>
      </main>
    </div>
  );
}
